import { useRef, useState, useEffect, useMemo } from "react";
import { Img } from "react-image";

import { useDocumentModal } from "../../common/CustomModal/CustomModalContext";
import HeaderSection from "../../DocPage/new-tab/headerSection";
import { api_without_cancellation } from "../../../api/api";
import { getCaseId, getClientId } from "../../../Utils/helper";

import { BrokenPreviewImage, photoName } from "./components/photo-view-card";

const CARD_WIDTH = 255;
const GAP = 5;

function mixColorWithWhite(hex, percentage) {
  const whitePercentage = (100 - percentage) / 100;

  let r = parseInt(hex?.slice(1, 3), 16);
  let g = parseInt(hex?.slice(3, 5), 16);
  let b = parseInt(hex?.slice(5, 7), 16);

  r = Math.floor(r + (255 - r) * whitePercentage);
  g = Math.floor(g + (255 - g) * whitePercentage);
  b = Math.floor(b + (255 - b) * whitePercentage);

  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

const NewPhotoShownOnPage = ({ data, activeTab, refetchData, data2 }) => {
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(1);
  const { showDocumentModal } = useDocumentModal();
  const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateLayout = () => {
      const containerWidth = el.getBoundingClientRect().width;
      const perRow = Math.floor((containerWidth + GAP) / (CARD_WIDTH + GAP));
      const validPerRow = perRow || 1;

      setCardsPerRow(validPerRow);
    };

    const observer = new ResizeObserver(updateLayout);
    observer.observe(el);
    updateLayout();

    return () => observer.disconnect();
  }, []);

  const sections = data?.data || [];
  const unsortedPhotos = data?.unsorted || [];

  const rows = useMemo(() => {
    if (!sections.length) return [];

    const allSlots = [];

    sections.forEach((section) => {
      const sectionName = section.page?.name || "Unsorted";
      const isPanelsEnabled = section.page?.panels || false;
      const sectionIcon =
        section.page?.page_icon ||
        "https://simplefirm-bucket.s3.amazonaws.com/static/images/photo-icon-color_2CyRX1a_GgdCtlG.svg";

      const pageSlots = section.slots || section.page_slots || [];

      if (isPanelsEnabled && section.panels && section.panels.length > 0) {
        const pageLevelSlots = pageSlots.filter((s) => s.slot_number === 0);
        pageLevelSlots.forEach((templateSlot) => {
          const slot = {
            slotInfo: templateSlot,
            sectionName,
            sectionIcon,
            isPanels: false,
            panelName: null,
            panelId: null,
            specialty: null,
            photo: null,
            isAttached: false,
          };

          const matchingDoc =
            section.page_docs?.find((doc) => doc.photo_slot?.id === templateSlot.id) ||
            templateSlot.photos?.[0];
          if (matchingDoc) {
            slot.photo = matchingDoc;
            slot.isAttached = true;
          }

          allSlots.push(slot);
        });

        section.panels.forEach((panel) => {
          panel.slots = panel.slots || [];

          if (panel.slots.length === 0) {
            pageSlots.forEach((templateSlot) => {
              if (templateSlot.slot_number !== 0) {
                panel.slots.push({ ...templateSlot, panel_id: panel.id });
              }
            });
          }

          panel.slots.forEach((slotInfo) => {
            const slot = {
              slotInfo,
              sectionName,
              sectionIcon,
              isPanels: true,
              panelName: panel.panel_name || null,
              panelId: panel.id || null,
              specialty: panel.specialty || null,
              photo: null,
              isAttached: false,
            };

            const matchingPhoto = panel.photos?.find(
              (photo) => photo.photo_slot?.id === slotInfo.id
            );
            if (matchingPhoto) {
              slot.photo = matchingPhoto;
              slot.isAttached = true;
            }

            allSlots.push(slot);
          });
        });
      } else {
        pageSlots.forEach((slotInfo) => {
          const slot = {
            slotInfo,
            sectionName,
            sectionIcon,
            isPanels: false,
            panelName: null,
            panelId: null,
            specialty: null,
            photo: null,
            isAttached: false,
          };

          const matchingDoc =
            section.page_docs?.find((doc) => doc.photo_slot?.id === slotInfo.id) ||
            slotInfo.photos?.[0];
          if (matchingDoc) {
            slot.photo = matchingDoc;
            slot.isAttached = true;
          }

          allSlots.push(slot);
        });
      }
    });

    const result = [];
    let currentIndex = 0;

    while (currentIndex < allSlots.length) {
      const rowSlots = allSlots.slice(currentIndex, currentIndex + cardsPerRow);
      const sectionGroups = {};

      rowSlots.forEach((slot) => {
        const key = slot.sectionName;
        if (!sectionGroups[key]) {
          sectionGroups[key] = {
            icon: slot.sectionIcon,
            isPanels: slot.isPanels,
            sectionName: slot.sectionName,
            slots: [],
          };
        }
        sectionGroups[key].slots.push({
          slotInfo: slot.slotInfo,
          photo: slot.photo,
          isAttached: slot.isAttached,
          panelId: slot.panelId,
          panelName: slot.panelName,
          specialty: slot.specialty,
        });
      });

      result.push(sectionGroups);
      currentIndex += cardsPerRow;
    }

    return result;
  }, [cardsPerRow, sections]);

  const handleFileChange = async (event, slot) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await api_without_cancellation.post(
        `${origin}/api/upload_photo/${getClientId()}/${getCaseId()}/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const result = response.data;

      const pageNameToModelName = {
        treatment: "CaseProviders",
        defendant: "Defendant",
        witnesses: "Witness",
        insurance: "Insurance",
        parties: "OtherParty",
        reports: "Reports",
        employment: "Employment",
        loans: "CaseLoan",
        discovery: "Discovery",
        recording: "Recordings",
        experts: "CaseExpert",
        Litigation: "LitigationAct",
      };
      const modelName = pageNameToModelName[slot?.slotInfo?.page?.name?.toLowerCase()] || "";

      const res = await api_without_cancellation.post("/api/attach_photo_to_page/", {
        page_id: slot?.slotInfo?.page?.id,
        case_id: getCaseId(),
        slot_id: slot?.slotInfo?.id,
        photo_id: result?.doc?.id,
        panel_id: slot?.panelId,
        model_name: modelName,
      });
      if (res.status === 200) refetchData();
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleFileChange2 = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await api_without_cancellation.post(
        `${origin}/api/upload_photo/${getClientId()}/${getCaseId()}/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const result = response.data;

      const res = await api_without_cancellation.post("/api/attach_photo_to_page/", {
        page_id: null,
        case_id: getCaseId(),
        slot_id: null,
        photo_id: result?.doc?.id,
        panel_id: null,
        model_name: "",
      });
      if (res.status === 200) refetchData();
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleDocPreview = (photo) => {
    showDocumentModal("photo", photo.image, photo, data2, refetchData, photo.case_thumbnail);
  };

  const cardWidthPx = `${CARD_WIDTH}px`;
  const imgWidth = CARD_WIDTH - 10;

  const labelBarBaseStyle = {
    color: "var(--primary)",
    width: cardWidthPx,
    minWidth: cardWidthPx,
    maxWidth: cardWidthPx,
    fontSize: "14px",
    fontWeight: "600",
    height: "25px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const computeHeaderWidth = (rowGroup, groupIndex, totalCardsInRow) => {
    const entries = Object.entries(rowGroup);
    const isLastSection = groupIndex === entries.length - 1;
    const cardsInThisSection = entries[groupIndex][1].slots.length;
    const shouldExpand = isLastSection && totalCardsInRow < cardsPerRow;
    return shouldExpand
      ? CARD_WIDTH * cardsInThisSection +
          GAP * (cardsInThisSection - 1) +
          (CARD_WIDTH + GAP) * (cardsPerRow - totalCardsInRow) +
          GAP
      : CARD_WIDTH * cardsInThisSection + GAP * (cardsInThisSection - 1);
  };

  const renderUnsortedPhotoCard = (photo) => (
    <div key={photo.id} className="d-flex flex-column align-items-center justify-content-center">
      <div
        className="has-primary-font-detail-view-card"
        style={{
          ...labelBarBaseStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "0px 10px",
          background: "var(--primary-5)",
        }}
      >
        Unsorted
      </div>

      <div
        className="font-weight-semibold cursor-pointer p-0 detailed-empty-card"
        id="no-vertical-border"
        onClick={() => handleDocPreview(photo)}
        style={{
          width: cardWidthPx,
          minWidth: cardWidthPx,
          maxWidth: cardWidthPx,
          minHeight: "322px",
          height: "322px",
          border: "none",
        }}
      >
        <div
          title={photo.title}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            padding: "0 5px",
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="https://simplefirm-bucket.s3.amazonaws.com/static/images/photo-icon-color_2CyRX1a_GgdCtlG.svg"
              className="ic ic-19 cursor-pointer img-19px"
              alt=""
            />
          </span>
          <p className="name new-view-title p-0" style={{ alignContent: "center" }}>
            {photoName(photo?.title, "")}
          </p>
        </div>
        <div className="detailed-empty-card-img-div" style={{ height: "297px" }}>
          {photo?.image ? (
            <Img
              src={photo.image}
              unloader={<BrokenPreviewImage />}
              style={{ height: 287, width: imgWidth, objectFit: "contain" }}
            />
          ) : (
            <BrokenPreviewImage />
          )}
        </div>
      </div>
    </div>
  );

  const renderUploadTile = (id) => (
    <div className="d-flex flex-column align-items-center justify-content-center">
      <div
        className="has-primary-font-detail-view-card"
        style={{
          ...labelBarBaseStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "0px 10px",
          background: "var(--primary-5)",
        }}
      >
        Save to Case
      </div>

      <div
        style={{ background: "var(--primary-4)" }}
        className="d-flex flex-column align-items-center justify-content-center"
      >
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{
            width: cardWidthPx,
            height: "322px",
            cursor: "pointer",
          }}
          onClick={() => document.getElementById(id)?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) handleFileChange2({ target: { files: [file] } });
          }}
        >
          <i className="ic ic-60 ic-placeholder-grey cursor-pointer m-t-10" />
          <p className="text-lg-grey m-t-5">Click or Drag to Upload</p>
          <input
            type="file"
            accept="image/*"
            id={id}
            style={{ display: "none" }}
            onChange={(e) => handleFileChange2(e)}
          />
        </div>
      </div>
    </div>
  );

  const renderUnsortedSection = (idPrefix) => {
    const totalCards = unsortedPhotos.length + 1; // photos + upload tile
    const headerWidth = CARD_WIDTH * totalCards + GAP * (totalCards - 1);

    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <HeaderSection
          name="Unsorted"
          icon="https://simplefirm-bucket.s3.amazonaws.com/static/images/photo-icon-color_2CyRX1a_GgdCtlG.svg"
          width={headerWidth}
        />
        <div style={{ display: "flex", gap: GAP, flexWrap: "wrap" }}>
          {unsortedPhotos.map((photo, i) => renderUnsortedPhotoCard(photo, i))}
          {renderUploadTile(`file-upload-${idPrefix}-icon`)}
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {rows.map((rowGroup, rowIndex) => {
        const totalCardsInRow = Object.values(rowGroup).reduce(
          (sum, page) => sum + page.slots.length,
          0
        );

        return (
          <div key={rowIndex} style={{ marginBottom: 5 }}>
            <div style={{ display: "flex", gap: GAP }}>
              {Object.entries(rowGroup).map(([sectionName, page], groupIndex) => {
                const headerWidth = computeHeaderWidth(rowGroup, groupIndex, totalCardsInRow);

                return (
                  <div key={groupIndex} style={{ display: "flex", flexDirection: "column" }}>
                    <HeaderSection name={sectionName} icon={page?.icon} width={headerWidth} />

                    <div style={{ display: "flex", gap: GAP }}>
                      {page?.slots?.map((slot, index) => {
                        if (slot?.isAttached) {
                          return (
                            <div
                              key={`${rowIndex}-${groupIndex}-${index}`}
                              className="d-flex flex-column align-items-center justify-content-center"
                            >
                              {sectionName === "Treatment" && (
                                <div
                                  className="has-primary-font-detail-view-card"
                                  style={{
                                    ...labelBarBaseStyle,
                                    display: "flex",
                                    justifyContent:
                                      slot?.panelName === null
                                        ? slot?.slotInfo?.slot_number === 0
                                          ? "center"
                                          : "flex-start"
                                        : "flex-start",
                                    alignItems: "center",
                                    background: slot?.specialty?.color
                                      ? mixColorWithWhite(slot?.specialty?.color, 10)
                                      : "var(--primary-5)",
                                  }}
                                >
                                  {slot?.panelName !== null && (
                                    <>
                                      <span
                                        className="d-flex m-r-5 align-items-center justify-content-center"
                                        style={{
                                          width: "25px",
                                          height: "25px",
                                          background: slot?.specialty?.color || "#000",
                                          color: "white",
                                          minWidth: "25px",
                                        }}
                                      >
                                        {slot?.specialty?.name?.[0] || "?"}
                                      </span>
                                      <span
                                        style={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          fontSize: "14px",
                                          fontWeight: "600",
                                          height: "25px",
                                          textTransform: "uppercase",
                                          alignContent: "center",
                                        }}
                                      >
                                        {slot?.panelName}
                                      </span>
                                    </>
                                  )}

                                  {slot?.panelName === null && (
                                    <>
                                      {slot?.slotInfo?.slot_number !== 0 && (
                                        <span
                                          className="d-flex m-r-5 align-items-center justify-content-center"
                                          style={{
                                            width: "25px",
                                            height: "25px",
                                            background: slot?.specialty?.color || "var(--primary)",
                                            color: "white",
                                            minWidth: "25px",
                                          }}
                                        >
                                          {slot?.specialty?.name?.[0] || "?"}
                                        </span>
                                      )}
                                      <span
                                        className="m-l-2"
                                        style={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          fontSize: "14px",
                                          fontWeight: "600",
                                          height: "25px",
                                          textTransform: "uppercase",
                                          alignContent: "center",
                                        }}
                                      >
                                        {slot?.slotInfo?.slot_number === 0
                                          ? "Attached"
                                          : slot?.slotInfo?.slot_name}
                                      </span>
                                    </>
                                  )}
                                </div>
                              )}

                              {sectionName !== "Treatment" && (
                                <div
                                  className="has-primary-font-detail-view-card text-center"
                                  style={{
                                    ...labelBarBaseStyle,
                                    display: slot?.slotInfo?.slot_number !== 0 && "flex",
                                    justifyContent: slot?.slotInfo?.slot_number !== 0 && "center",
                                    alignItems: slot?.slotInfo?.slot_number !== 0 && "center",
                                    alignContent: slot?.slotInfo?.slot_number === 0 && "center",
                                    padding: "0px 10px",
                                    background: "var(--primary-5)",
                                  }}
                                >
                                  {sectionName
                                    ? slot?.slotInfo?.slot_number === 0
                                      ? "Attached"
                                      : slot?.panelName ||
                                        (slot?.slotInfo?.slot_name === null && "Available") ||
                                        slot?.slotInfo?.slot_name ||
                                        "Unattached Photo"
                                    : "Unattached Photo"}
                                </div>
                              )}

                              <div
                                className="font-weight-semibold cursor-pointer p-0 detailed-empty-card"
                                id="no-vertical-border"
                                onClick={() => handleDocPreview(slot?.photo)}
                                style={{
                                  width: cardWidthPx,
                                  minWidth: cardWidthPx,
                                  maxWidth: cardWidthPx,
                                  minHeight: "322px",
                                  height: "322px",
                                  border: "none",
                                }}
                              >
                                <div
                                  title={slot?.photo?.title || slot?.slotInfo?.slot_name}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    padding: "0 5px",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <img
                                      src="https://simplefirm-bucket.s3.amazonaws.com/static/images/photo-icon-color_2CyRX1a_GgdCtlG.svg"
                                      className="ic ic-19 cursor-pointer img-19px"
                                      alt=""
                                    />
                                  </span>
                                  <p
                                    className="name new-view-title p-0"
                                    style={{ alignContent: "center" }}
                                  >
                                    {slot?.slotInfo?.slot_number === 0
                                      ? ""
                                      : slot?.slotInfo === null
                                        ? ""
                                        : `${slot?.slotInfo?.slot_number} .`}{" "}
                                    {photoName(slot?.photo?.title, slot?.slotInfo?.slot_name)}
                                  </p>
                                </div>
                                <div
                                  className="detailed-empty-card-img-div"
                                  style={{ height: "297px" }}
                                >
                                  {slot?.photo?.image ? (
                                    <Img
                                      src={slot?.photo?.image}
                                      unloader={<BrokenPreviewImage />}
                                      style={{
                                        height: 287,
                                        width: imgWidth,
                                        objectFit: "contain",
                                      }}
                                    />
                                  ) : (
                                    <BrokenPreviewImage />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Empty / unattached slot — upload tile
                        return (
                          <div
                            key={`${rowIndex}-${groupIndex}-${index}`}
                            className="d-flex flex-column align-items-center justify-content-center"
                          >
                            {sectionName === "Treatment" && (
                              <div
                                className="has-primary-font-detail-view-card"
                                style={{
                                  ...labelBarBaseStyle,
                                  display: "flex",
                                  justifyContent:
                                    slot?.panelName === null
                                      ? slot?.slotInfo?.slot_number === 0
                                        ? "center"
                                        : "flex-start"
                                      : "flex-start",
                                  alignItems: "center",
                                  background: slot?.specialty?.color
                                    ? mixColorWithWhite(slot?.specialty?.color, 10)
                                    : "var(--primary-5)",
                                }}
                              >
                                {slot?.panelName !== null && (
                                  <>
                                    <span
                                      className="d-flex m-r-5 align-items-center justify-content-center"
                                      style={{
                                        width: "25px",
                                        height: "25px",
                                        background: slot?.specialty?.color || "#000",
                                        color: "white",
                                        minWidth: "25px",
                                      }}
                                    >
                                      {slot?.specialty?.name?.[0] || "?"}
                                    </span>
                                    <span
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        height: "25px",
                                        textTransform: "uppercase",
                                        alignContent: "center",
                                      }}
                                    >
                                      {slot?.panelName}
                                    </span>
                                  </>
                                )}

                                {slot?.panelName === null && (
                                  <>
                                    {slot?.slotInfo?.slot_number !== 0 && (
                                      <span
                                        className="d-flex m-r-5 align-items-center justify-content-center"
                                        style={{
                                          width: "25px",
                                          height: "25px",
                                          background: slot?.specialty?.color || "var(--primary)",
                                          color: "white",
                                          minWidth: "25px",
                                        }}
                                      >
                                        {slot?.specialty?.name?.[0] || ""}
                                      </span>
                                    )}
                                    <span
                                      className="m-l-2"
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        height: "25px",
                                        textTransform: "uppercase",
                                        alignContent: "center",
                                      }}
                                    >
                                      {slot?.slotInfo?.slot_name}
                                    </span>
                                  </>
                                )}
                              </div>
                            )}

                            {sectionName !== "Treatment" && (
                              <div
                                className="has-primary-font-detail-view-card"
                                style={{
                                  ...labelBarBaseStyle,
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  padding: "0px 10px",
                                  background: "var(--primary-5)",
                                }}
                              >
                                {sectionName
                                  ? slot.panelName ||
                                    (slot?.slotInfo?.slot_number === 0 &&
                                      slot?.slotInfo?.slot_name) ||
                                    (slot?.slotInfo?.slot_name === null && "Available") ||
                                    slot?.slotInfo?.slot_name ||
                                    "Unattached Photo"
                                  : "Unattached Photo"}
                              </div>
                            )}

                            <div
                              className="d-flex flex-column align-items-center justify-content-center"
                              style={{
                                width: cardWidthPx,
                                height: "322px",
                                cursor: "pointer",
                                background: "var(--primary-4)",
                              }}
                              onClick={() =>
                                document
                                  .getElementById(`file-upload-${rowIndex}-${groupIndex}-${index}`)
                                  ?.click()
                              }
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => {
                                e.preventDefault();
                                const file = e.dataTransfer.files[0];
                                if (file) {
                                  handleFileChange({ target: { files: [file] } }, slot);
                                }
                              }}
                            >
                              <i className="ic ic-60 ic-placeholder-grey cursor-pointer m-t-10" />
                              <p className="text-lg-grey m-t-5">Click or Drag to Upload</p>
                              <input
                                type="file"
                                accept="image/*"
                                id={`file-upload-${rowIndex}-${groupIndex}-${index}`}
                                style={{ display: "none" }}
                                onChange={(e) => handleFileChange(e, slot)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {(activeTab === "all" || activeTab === "unsorted") && renderUnsortedSection("unsorted-row")}
    </div>
  );
};

export default NewPhotoShownOnPage;
